import React, { useState, useEffect } from 'react';
import { facilityAPI } from '../services/api';
import { FileText, Download, Loader, AlertCircle, CheckCircle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DOC_TYPES = [
  { id: 'permit_renewal', title: 'Permit Renewal Package', desc: 'Complete air permit renewal application', icon: '📋', color: '#0066FF', bg: '#EFF6FF' },
  { id: 'annual_report', title: 'Annual Compliance Report', desc: 'Comprehensive annual air quality report', icon: '📊', color: '#16A34A', bg: '#F0FDF4' },
  { id: 'semi_annual_report', title: 'Semi-Annual Report (SAMR)', desc: '6-month monitoring and compliance summary', icon: '📈', color: '#7C3AED', bg: '#F5F3FF' },
  { id: 'quarterly_emissions', title: 'Quarterly Emissions Report', desc: 'Quarterly emissions data and calculations', icon: '🔢', color: '#D97706', bg: '#FFFBEB' },
  { id: 'stack_test_protocol', title: 'Stack Test Protocol', desc: 'Detailed stack testing protocol and procedures', icon: '🔬', color: '#0891B2', bg: '#ECFEFF' },
  { id: 'deviation_report', title: 'Deviation Report', desc: 'Report for permit deviations or limit exceedances', icon: '⚠️', color: '#DC2626', bg: '#FEF2F2' },
  { id: 'title_v_certification', title: 'Title V Annual Certification', desc: 'Annual compliance certification for Title V permits', icon: '✅', color: '#16A34A', bg: '#F0FDF4' },
  { id: 'ssm_report', title: 'SSM Report', desc: 'Startup, Shutdown & Malfunction event report', icon: '🚨', color: '#DC2626', bg: '#FEF2F2' },
  { id: 'emissions_inventory', title: 'Emissions Inventory Report', desc: 'Annual state emissions inventory using logged data', icon: '🏭', color: '#0066FF', bg: '#EFF6FF' },
  { id: 'permit_modification', title: 'Permit Modification Request', desc: 'Request to modify existing permit conditions', icon: '✏️', color: '#7C3AED', bg: '#F5F3FF' },
  { id: 'inspection_checklist', title: 'Inspection Preparation Checklist', desc: 'Everything to have ready when the inspector arrives', icon: '🗂️', color: '#0891B2', bg: '#ECFEFF' },
  { id: 'nsr_pre_application', title: 'NSR Pre-Application', desc: 'New Source Review pre-application package', icon: '🏗️', color: '#D97706', bg: '#FFFBEB' },
];

function DocumentGenerator() {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [generating, setGenerating] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);

  useEffect(() => { loadFacilities(); }, []);

  const loadFacilities = async () => {
    try {
      const res = await facilityAPI.getAll();
      const facs = res.data.facilities || [];
      setFacilities(facs);
      if (facs.length > 0) setSelectedFacility(facs[0].id);
    } catch (err) {
      toast.error('Failed to load facilities');
    }
  };

  const handleGenerate = async (docType, skipInfo = false) => {
    if (!selectedFacility) { toast.error('Please select a facility'); return; }
    if (!skipInfo && ['permit_renewal', 'stack_test_protocol', 'deviation_report', 'ssm_report', 'permit_modification', 'nsr_pre_application'].includes(docType)) {
      setSelectedDocType(docType);
      setShowInfoModal(true);
      return;
    }

    setGenerating(true);
    setCurrentDoc(docType);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/document-generator/generate`,
        { documentType: docType, facilityId: selectedFacility, additionalInfo: additionalInfo || null },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const facility = facilities.find(f => f.id === parseInt(selectedFacility));
      link.setAttribute('download', `${docType.replace(/_/g, '-')}-${facility?.name || 'document'}-${Date.now()}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Document generated and downloaded!');
      setAdditionalInfo('');
      setShowInfoModal(false);
      setSelectedDocType(null);
    } catch (err) {
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
      setCurrentDoc(null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div className="page-title">Generate Documents</div>
        <div className="page-subtitle">AI-powered compliance document creation · Download as Word (.docx)</div>
      </div>

      {/* Facility selector */}
      <div className="card" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>Generate for:</div>
        <select value={selectedFacility} onChange={e => setSelectedFacility(e.target.value)} style={{ maxWidth: 320 }}>
          {facilities.length === 0 ? (
            <option>No facilities — add one first</option>
          ) : (
            facilities.map(f => (
              <option key={f.id} value={f.id}>{f.name || f.facility_name} {f.state ? `· ${f.state}` : ''}</option>
            ))
          )}
        </select>
      </div>

      {/* Document grid */}
      <div className='docgen-grid' style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {DOC_TYPES.map(doc => (
          <div key={doc.id} className="card" style={{ padding: '18px 20px', cursor: 'pointer', transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = doc.color}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#E2E8F0'}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: 9, background: doc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{doc.icon}</div>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628', marginBottom: 3 }}>{doc.title}</div>
                <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'var(--font-body)', lineHeight: 1.4 }}>{doc.desc}</div>
              </div>
            </div>
            <button
              onClick={() => !generating && handleGenerate(doc.id)}
              disabled={generating || !selectedFacility}
              style={{
                width: '100%', padding: '8px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                fontFamily: 'var(--font-body)', cursor: generating ? 'not-allowed' : 'pointer',
                background: generating && currentDoc === doc.id ? '#F8FAFC' : doc.bg,
                color: doc.color, border: `1px solid ${doc.color}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                opacity: !selectedFacility ? 0.5 : 1
              }}
            >
              {generating && currentDoc === doc.id ? (
                <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>
              ) : (
                <><Download size={14} /> Generate</>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Tips */}
      <div className="card" style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <CheckCircle size={18} color="#16A34A" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 4 }}>Pro tips for better documents</div>
            <div style={{ fontSize: 14, color: '#166534', fontFamily: 'var(--font-body)', lineHeight: 1.6 }}>
              Upload your air permit first · Add state regulations to Knowledge Base · Review and edit before submitting · Documents include [INSERT DATA] placeholders where facility-specific info is needed
            </div>
          </div>
        </div>
      </div>

      {/* Additional info modal */}
      {showInfoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 520 }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>Additional Context</div>
              <button onClick={() => { setShowInfoModal(false); setSelectedDocType(null); }} className="icon-btn"><X size={16} /></button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)', marginBottom: 14 }}>
                Add any specific details to help the AI generate a more accurate document:
              </div>
              <textarea
                value={additionalInfo}
                onChange={e => setAdditionalInfo(e.target.value)}
                rows={5}
                placeholder="e.g. 'Deviation occurred on March 15 due to power outage', 'Include new baghouse installed in Q2', 'Testing scheduled for April 2026'..."
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button onClick={() => { setShowInfoModal(false); setSelectedDocType(null); setAdditionalInfo(''); }} className="btn btn-ghost" disabled={generating}>Cancel</button>
                <button onClick={() => handleGenerate(selectedDocType, true)} className="btn btn-primary" disabled={generating} style={{ opacity: generating ? 0.6 : 1, cursor: generating ? 'not-allowed' : 'pointer' }}>
                  {generating ? '⏳ Generating...' : 'Generate Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <style>{`
      .docgen-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px; }
      @media (max-width: 900px) { .docgen-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      @media (max-width: 480px) { .docgen-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; } }
      @media (max-width: 768px) {
        .docgen-header { flex-direction: column !important; }
        .docgen-header .btn { width: 100% !important; justify-content: center !important; }
      }
    `}</style>
    </div>
  );
}

export default DocumentGenerator;
